// Project detail → Members (architecture sprint-12 §9.1, A-7). One component for Admins and Supervisors, on the
// oversight endpoints. Read-only viewers (viewTeam) see the list and pending invites without any action;
// managers (Admins, Supervisors with manageMembers) add, change, remove, renew and cancel. The server decides
// every row's rights (canEdit, canRenew); the API is the arbiter.
import { MoreHorizontal, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { LinkRevealDialog, type RevealedLink } from '@/components/LinkRevealDialog';
import { SectionTitle } from '@/components/SectionTitle';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useCancelInviteGrant, useRenewInviteGrant } from '@/hooks/use-project-invites';
import { useChangeMemberRole, useProjectMembers, useRemoveMember } from '@/hooks/use-project-members';
import { formatDateTime, formatShortLocalDate } from '@/lib/format';
import { inviteReveal } from '@/pages/users/InvitesTab';
import type { ProjectInvite, ProjectMember, ProjectRole } from '@/types/settings';
import { AddMembersDialog } from './AddMembersDialog';

export const ROLE_LABEL: Record<ProjectRole, string> = { member: 'Member', supervisor: 'Supervisor' };
export const CANCEL_GRANT_BODY = 'Their invite to this project is cancelled. If it was only for this project, the invite link stops working.';
export const REMOVE_BODY = 'Their existing entries and any running timer stay as they are.';

interface ProjectMembersTabProps {
  projectId: string;
  /** The signed-in person (their own row shows "You"). */
  viewerId: string;
  isAdmin: boolean;
  /** caps.addNotes: each row offers "Add note". */
  onAddNote?: (member: ProjectMember) => void;
}

function MemberRow({
  member,
  isYou,
  pending,
  onRole,
  onRemove,
  onAddNote,
}: {
  member: ProjectMember;
  isYou: boolean;
  pending: boolean;
  onRole: (role: ProjectRole) => void;
  onRemove: () => void;
  onAddNote?: () => void;
}): JSX.Element {
  const hasActions = member.canEdit || onAddNote !== undefined;
  return (
    <TableRow>
      <TableCell>
        <span className="inline-flex flex-wrap items-center gap-1.5">
          {member.user.name}
          {isYou && <Badge variant="outline">You</Badge>}
          {member.isGlobalAdmin && <Badge variant="primary">Admin</Badge>}
          {member.user.status === 'deactivated' && <Badge>Deactivated</Badge>}
        </span>
      </TableCell>
      <TableCell className="text-muted-foreground">{member.user.email}</TableCell>
      <TableCell>
        {member.canEdit ? (
          <NativeSelect
            aria-label={`Role for ${member.user.name}`}
            className="w-36"
            value={member.role}
            disabled={pending}
            onChange={(e) => onRole(e.target.value === 'supervisor' ? 'supervisor' : 'member')}
          >
            <option value="member">Member</option>
            <option value="supervisor">Supervisor</option>
          </NativeSelect>
        ) : (
          <span className="text-sm text-foreground">{ROLE_LABEL[member.role]}</span>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground">{formatShortLocalDate(member.addedAt.slice(0, 10))}</TableCell>
      <TableCell>
        {hasActions && (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${member.user.name}`}>
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onAddNote && <DropdownMenuItem onSelect={onAddNote}>Add note</DropdownMenuItem>}
              {member.canEdit && <DropdownMenuItem onSelect={onRemove}>Remove</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  );
}

function InviteRow({ invite, canManage, onRenew, onCancel }: { invite: ProjectInvite; canManage: boolean; onRenew: () => void; onCancel: () => void }): JSX.Element {
  return (
    <TableRow>
      <TableCell>{invite.email}</TableCell>
      <TableCell>{ROLE_LABEL[invite.role]}</TableCell>
      <TableCell className="font-mono tabular-nums">
        {invite.expired ? <Badge variant="danger">Expired</Badge> : formatDateTime(invite.expiresAt)}
      </TableCell>
      <TableCell className="text-muted-foreground">{invite.addedBy.name}</TableCell>
      <TableCell>
        {canManage && (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label={`Actions for the invite to ${invite.email}`}>
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {invite.canRenew && <DropdownMenuItem onSelect={onRenew}>New link</DropdownMenuItem>}
              <DropdownMenuItem onSelect={onCancel}>Cancel invite</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  );
}

export function ProjectMembersTab({ projectId, viewerId, isAdmin, onAddNote }: ProjectMembersTabProps): JSX.Element {
  const members = useProjectMembers(projectId);
  const changeRole = useChangeMemberRole(projectId);
  const remove = useRemoveMember(projectId);
  const cancelGrant = useCancelInviteGrant(projectId);
  const renew = useRenewInviteGrant(projectId);
  const [addOpen, setAddOpen] = useState(false);
  const [removing, setRemoving] = useState<ProjectMember | null>(null);
  const [renewing, setRenewing] = useState<ProjectInvite | null>(null);
  const [cancelling, setCancelling] = useState<ProjectInvite | null>(null);
  const [revealed, setRevealed] = useState<RevealedLink | null>(null);

  if (!members.data) return <Skeleton className="h-40 w-full" />;
  const { items, pendingInvites, viewer } = members.data;
  const canManage = viewer.canManage;

  return (
    <div className="space-y-6">
      <section>
        <SectionTitle
          title={`${items.length} ${items.length === 1 ? 'member' : 'members'}`}
          actions={
            canManage && (
              <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
                <UserPlus aria-hidden="true" />
                Add members
              </Button>
            )
          }
        />
        {items.length === 0 ? (
          <EmptyState variant="card" title="No members yet" hint="Only members can see this project and log time on it." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="w-10">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((m) => (
                <MemberRow
                  key={m.user.id}
                  member={m}
                  isYou={m.user.id === viewerId}
                  pending={changeRole.isPending}
                  onRole={(role) => changeRole.mutate({ userId: m.user.id, role }, { onSuccess: () => toast.success('Role changed.') })}
                  onRemove={() => setRemoving(m)}
                  onAddNote={onAddNote ? () => onAddNote(m) : undefined}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </section>

      {pendingInvites.length > 0 && (
        <section>
          <SectionTitle title="Pending invites" />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Added by</TableHead>
                <TableHead className="w-10">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingInvites.map((invite) => (
                <InviteRow key={invite.grantId} invite={invite} canManage={canManage} onRenew={() => setRenewing(invite)} onCancel={() => setCancelling(invite)} />
              ))}
            </TableBody>
          </Table>
        </section>
      )}

      {canManage && (
        <AddMembersDialog
          projectId={projectId}
          isAdmin={isAdmin}
          existing={new Set(items.map((m) => m.user.id))}
          open={addOpen}
          onOpenChange={setAddOpen}
          onInvited={(link, email, expiresAt) => {
            setAddOpen(false);
            setRevealed(inviteReveal(link, email, expiresAt));
          }}
        />
      )}
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={`Remove ${removing?.user.name ?? ''}?`}
        body={<p>{REMOVE_BODY}</p>}
        confirmLabel="Remove"
        destructive
        pending={remove.isPending}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.user.id, {
            onSuccess: () => {
              toast.success('Member removed.');
              setRemoving(null);
            },
            onError: () => setRemoving(null),
          })
        }
      />
      <ConfirmDialog
        open={renewing !== null}
        onOpenChange={(o) => !o && setRenewing(null)}
        title="Generate a new link?"
        body="The current link stops working."
        confirmLabel="Generate link"
        pending={renew.isPending}
        onConfirm={() =>
          renewing &&
          renew.mutate(renewing.grantId, {
            onSuccess: ({ invite, link }) => {
              setRenewing(null);
              setRevealed(inviteReveal(link, invite.email, invite.expiresAt));
            },
            onError: () => setRenewing(null),
          })
        }
      />
      <ConfirmDialog
        open={cancelling !== null}
        onOpenChange={(o) => !o && setCancelling(null)}
        title="Cancel this invite?"
        body={CANCEL_GRANT_BODY}
        confirmLabel="Cancel invite"
        destructive
        pending={cancelGrant.isPending}
        onConfirm={() =>
          cancelling &&
          cancelGrant.mutate(cancelling.grantId, {
            onSuccess: () => {
              toast.success('Invite cancelled.');
              setCancelling(null);
            },
            onError: () => setCancelling(null),
          })
        }
      />
      {/* Closing drops the link from state: it is never shown again. */}
      <LinkRevealDialog value={revealed} onClose={() => setRevealed(null)} />
    </div>
  );
}
